import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ExternalLink, Play, X } from 'lucide-react';
import { pageTransition, pageVariants } from '../constants/pageMotion';

const EMPTY_ESTADISTICAS = { preocupaciones: [], tendencias: [], fuente: '' };

export default function Datos({
  articulos = [],
  videos = [],
  estadisticasElectorado = EMPTY_ESTADISTICAS
}) {
  const { preocupaciones = [], tendencias = [], fuente = '' } = estadisticasElectorado ?? EMPTY_ESTADISTICAS;
  const [videoActivo, setVideoActivo] = useState(null);

  return (
    <motion.section
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      className="max-w-7xl mx-auto px-4 py-10 md:px-6"
    >
      <div className="text-center mb-10">
        <span className="bg-slate-100 text-slate-600 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest">Centro de recursos</span>
        <h2 className="mt-5 text-3xl font-extrabold leading-tight sm:text-4xl md:text-5xl">Análisis y Multimedia</h2>
        <div className="flex justify-center gap-1.5 my-5">
          <span className="w-8 h-[2px] bg-amber-200 rounded-full" />
          <span className="w-8 h-[2px] bg-blue-200 rounded-full" />
          <span className="w-8 h-[2px] bg-red-200 rounded-full" />
        </div>
        <p className="text-slate-500 max-w-3xl mx-auto mt-4">Artículos de análisis, debates y entrevistas integradas en una sola experiencia. Las gráficas de intención de voto quedan como apoyo contextual al final.</p>
      </div>

      <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px] items-start">
        <div className="space-y-10">
          <section>
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Artículos de análisis</p>
                <h3 className="mt-2 text-2xl font-bold">Lecturas para entender la campaña</h3>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-2">
              {articulos.map((articulo) => (
                <motion.article
                  key={articulo.id}
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img src={articulo.imagen} alt={articulo.titulo} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                    <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-700">
                      {articulo.fuente}
                    </span>
                  </div>
                  <div className="p-6">
                    <h4 className="text-xl font-extrabold leading-tight text-slate-900">{articulo.titulo}</h4>
                    <p className="mt-3 text-sm leading-7 text-slate-600">{articulo.resumen}</p>
                    <a
                      href={articulo.url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-700"
                    >
                      Leer más
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </motion.article>
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Debates y entrevistas</p>
                <h3 className="mt-2 text-2xl font-bold">Videos integrados de YouTube</h3>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {videos.map((video) => (
                <motion.button
                  key={video.id}
                  type="button"
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => setVideoActivo(video)}
                  className="group overflow-hidden rounded-3xl border border-slate-100 bg-white text-left shadow-sm w-full"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img src={video.thumbnail} alt={video.titulo} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow-lg transition-transform duration-300 group-hover:scale-110">
                        <Play className="h-7 w-7 fill-current" />
                      </span>
                    </div>
                    <span className="absolute left-4 top-4 rounded-full bg-black/60 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                      {video.fuente}
                    </span>
                  </div>
                  <div className="p-6">
                    <h4 className="text-lg font-bold leading-tight text-slate-900">{video.titulo}</h4>
                    <p className="mt-3 text-sm leading-7 text-slate-600">{video.descripcion}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                      Ver video
                      <Play className="h-4 w-4" />
                    </span>
                  </div>
                </motion.button>
              ))}
            </div>
          </section>
        </div>

        <aside className="xl:sticky xl:top-24 space-y-6 rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Indicadores rápidos</p>
            <h3 className="mt-2 text-xl font-bold">Preocupaciones del electorado</h3>
          </div>

          <div className="space-y-4">
            {preocupaciones.map((item) => (
              <div key={item.id}>
                <div className="mb-2 flex items-center justify-between gap-3 text-sm font-medium text-slate-600">
                  <span>{item.label}</span>
                  <span>{item.value}%</span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${item.value}%` }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    className={`${item.color} h-full rounded-full`}
                  />
                </div>
              </div>
            ))}
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Tendencias electorales</p>
            <div className="mt-4 space-y-5">
              {tendencias.map((item) => (
                <div key={item.id} className="flex items-center gap-4">
                  <div className="relative h-20 w-20 shrink-0 rounded-full bg-slate-100">
                    <div className="absolute inset-2 flex items-center justify-center rounded-full bg-white shadow-sm">
                      <span className="text-lg font-extrabold">{item.value}%</span>
                    </div>
                    <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 120 120" aria-hidden="true">
                      <circle cx="60" cy="60" r="52" stroke="#e2e8f0" strokeWidth="10" fill="none" />
                      <circle
                        cx="60"
                        cy="60"
                        r="52"
                        stroke="currentColor"
                        className="text-slate-800"
                        strokeWidth="10"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 52}`}
                        strokeDashoffset={`${2 * Math.PI * 52 * (1 - item.value / 100)}`}
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-slate-600">{item.label}</p>
                </div>
              ))}
            </div>
          </div>

          {fuente && (
            <p className="text-[11px] leading-5 text-slate-400 border-t border-slate-100 pt-4">{fuente}</p>
          )}
        </aside>
      </div>

      <AnimatePresence>
        {videoActivo?.url && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 py-8 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              className="absolute inset-0 cursor-default bg-transparent border-0"
              aria-label="Cerrar video"
              onClick={() => setVideoActivo(null)}
            />

            <motion.div
              initial={{ scale: 0.96, y: 12 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 12 }}
              transition={{ duration: 0.2 }}
              className="relative z-10 w-full max-w-5xl overflow-hidden rounded-[2rem] bg-white shadow-2xl"
            >
              <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Reproducción integrada</p>
                  <h4 className="mt-1 text-lg font-bold text-slate-900">{videoActivo.titulo}</h4>
                </div>
                <button
                  type="button"
                  onClick={() => setVideoActivo(null)}
                  className="inline-flex items-center justify-center rounded-full border border-slate-200 p-2 text-slate-600 transition-colors hover:bg-slate-50"
                  aria-label="Cerrar"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="aspect-video bg-black">
                <iframe
                  src={videoActivo.url}
                  title={videoActivo.titulo}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}
